import React, { useContext, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import { Box, Grid, Card, CardContent, Typography, Avatar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Skeleton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PieChartIcon from '@mui/icons-material/PieChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';

const recentActivity = [
  {
    name: 'Sierra Ferguson',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    description: 'Upgraded to Premium',
    time: '6 hours ago',
    email: 's.ferguson@gmail.com',
    phone: '+989 (98) 436-46-115',
  },
  {
    name: 'Sierra Ferguson',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    description: 'Changed profile settings',
    time: '6 hours ago',
    email: 's.ferguson@gmail.com',
    phone: '+989 (98) 436-46-115',
  },
  {
    name: 'Sierra Ferguson',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    description: 'Activated new feature',
    time: '7 hours ago',
    email: 's.ferguson@gmail.com',
    phone: '+989 (98) 436-46-115',
  },
];

const Main = () => {
  const api = useContext(CONTEXTS.APIContext);
  const adminController = useMemo(() => new CONTROLLERS.AdminController(api), [api]);

  const [loading, setLoading] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [figureDraftStats, setFigureDraftStats] = useState(null);
  const [pendingCompanyDraftsCount, setPendingCompanyDraftsCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const [u, f, c] = await Promise.all([
          adminController.getUserStats(),
          adminController.getFigureDraftStats(),
          adminController.getPendingCompanyDrafts({ skip: 0, take: 1 }),
        ]);

        if (!mounted) return;
        setUserStats(u || null);
        setFigureDraftStats(f || null);
        setPendingCompanyDraftsCount(c?.meta?.totalItems || 0);
      } catch (e) {
        console.error('Failed to load dashboard stats:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [adminController]);

  const stats = [
    {
      label: 'Total Users',
      value: userStats?.totalUsers?.toLocaleString?.() || '0',
      icon: <PeopleIcon color="primary" />,
      change: `${userStats?.percentageChanges?.totalUsers >= 0 ? '+' : ''}${userStats?.percentageChanges?.totalUsers || 0}%`,
      changeText: 'from yesterday',
      changeColor: userStats?.percentageChanges?.totalUsers >= 0 ? 'success.main' : 'error.main',
    },
    {
      label: 'Premium Users',
      value: userStats?.premiumUsers?.toLocaleString?.() || '0',
      icon: <VerifiedUserIcon color="info" />,
      change: `${userStats?.percentageChanges?.activeUsers >= 0 ? '+' : ''}${userStats?.percentageChanges?.activeUsers || 0}%`,
      changeText: 'active change',
      changeColor: userStats?.percentageChanges?.activeUsers >= 0 ? 'success.main' : 'error.main',
    },
    {
      label: 'Pending Figure Drafts',
      value: String(figureDraftStats?.pending ?? 0),
      icon: <MonetizationOnIcon color="warning" />,
      change: '',
      changeText: '',
      changeColor: 'text.secondary',
    },
    {
      label: 'Pending Company Drafts',
      value: String(pendingCompanyDraftsCount ?? 0),
      icon: <PieChartIcon color="success" />,
      change: '',
      changeText: '',
      changeColor: 'text.secondary',
    },
  ];

  return (
    <MainLayout>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>Dashboard</Typography>
        <Typography variant="subtitle2" color="text.secondary" mb={3}>
          Hi, Samantha. Welcome back to <b>FigShelf</b> Admin!
        </Typography>
        <Grid container spacing={2} mb={3}>
          {loading
            ? Array.from({ length: 4 }).map((_, idx) => (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton width="40%" height={28} />
                          <Skeleton width="70%" height={18} />
                        </Box>
                      </Box>
                      <Skeleton width="60%" height={16} />
                    </CardContent>
                  </Card>
                </Grid>
              ))
            : stats.map((stat) => (
                <Grid item xs={12} sm={6} md={3} key={stat.label}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ bgcolor: 'background.paper', mr: 2 }}>{stat.icon}</Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight={700}>{stat.value}</Typography>
                          <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                        </Box>
                      </Box>
                      {(stat.change || stat.changeText) && (
                        <Typography variant="caption" sx={{ color: stat.changeColor, fontWeight: 500 }}>
                          {stat.change} {stat.changeText}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
        </Grid>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>Pie Chart</Typography>
                {/* Placeholder for Pie Chart */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', height: 120 }}>
                  <Box>
                    <Typography variant="h6" color="success.main">81%</Typography>
                    <Typography variant="caption">Total Order</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" color="info.main">22%</Typography>
                    <Typography variant="caption">Customer Growth</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" color="warning.main">62%</Typography>
                    <Typography variant="caption">Total Revenue</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>Sales Details</Typography>
                {/* Placeholder for Sales Details Chart */}
                <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                  <Typography variant="caption">[Sales Chart Placeholder]</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>Total Revenue</Typography>
                {/* Placeholder for Revenue Line Chart */}
                <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                  <Typography variant="caption">[Revenue Line Chart Placeholder]</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>Customer Map</Typography>
                {/* Placeholder for Customer Map Chart */}
                <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                  <Typography variant="caption">[Customer Map Chart Placeholder]</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>Recent Activity</Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Users</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>View All</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentActivity.map((activity, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={activity.avatar} alt={activity.name} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{activity.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{activity.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{activity.description}</TableCell>
                      <TableCell>{activity.time}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default Main;